import {useCallback, useEffect, useEffectEvent, useRef, useState} from 'react';
import type {HttpError} from 'api/http/types';
import type {DataLoadStatus} from '../types';

export const useLazyGetResourceWithRefresh = <TData>(
  doLoad: boolean,
  get: () => Promise<TData>,
): {result: DataLoadStatus<TData>; refresh: () => void} => {
  const doLoadRef = useRef(false);
  const [result, setResult] = useState<DataLoadStatus<TData>>({
    status: 'NotLoaded',
  });
  const onSetResult = useEffectEvent(() => {
    setResult(old => ({status: 'Loading', data: old.data}));
    get()
      .then(data => {
        setResult(() => ({status: 'Loaded', data}));
      })
      .catch((error: HttpError) => {
        setResult(old => ({status: 'Failed', error, data: old.data}));
      });
  });

  const caller = useCallback(() => {
    setResult(old => ({status: 'Loading', data: old.data}));
    get()
      .then(data => {
        setResult(() => ({status: 'Loaded', data}));
      })
      .catch((error: HttpError) => {
        setResult(old => ({status: 'Failed', error, data: old.data}));
      });
  }, [get]);

  useEffect(() => {
    // Trigger loading first time doLoad is true
    if (doLoad && !doLoadRef.current) {
      doLoadRef.current = true;

      onSetResult();
    }
  }, [doLoad, caller, result.status]);

  return {
    result,
    refresh: () => caller(),
  };
};
